import { useState, useCallback, useEffect } from 'react';
import { Paper, Tooltip } from '@mui/material';
import { Spinner, Input } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getInspectionTypes,
  createInspectionType,
  updateInspectionType,
  deleteInspectionType,
} from './FormikHelpers/Api';
import './inspection-tracker.scss';

const emptyType = { name: '', defaultContractor: '', description: '', countsSuiteInspections: false };

const SUITE_NAME_RE = /suite\s*inspection/i;
// Legacy type docs have no flag; treat a "Suite Inspection" name as opted in.
const countsSuite = (t) => t?.countsSuiteInspections === true || (t?.countsSuiteInspections == null && SUITE_NAME_RE.test(t?.name || ''));

const FInput = ({ value, onChange, placeholder, readOnly, type = 'text' }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value || ''}
    onChange={(e) => !readOnly && onChange(e.target.value)}
    placeholder={placeholder}
    readOnly={readOnly}
    style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: readOnly ? '#f8f9fa' : undefined }}
  />
);

const InlineTypeRow = ({ data, isNew, onSave, onCancel }) => {
  const [row, setRow] = useState({ ...data, countsSuiteInspections: countsSuite(data) });
  const [saving, setSaving] = useState(false);
  // Once the user has touched the checkbox, the name no longer auto-ticks it.
  const [touchedSuite, setTouchedSuite] = useState(false);

  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));
  const setName = (v) => setRow((p) => ({
    ...p,
    name: v,
    countsSuiteInspections: (!touchedSuite && SUITE_NAME_RE.test(v)) ? true : p.countsSuiteInspections,
  }));
  const setCountsSuite = (checked) => { setTouchedSuite(true); set('countsSuiteInspections')(checked); };

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error('Name is required'); return; }
    if (!row.defaultContractor?.trim()) { toast.error('Default contractor is required'); return; }
    setSaving(true);
    try {
      await onSave(row);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={`it-editable-row it-editable-row--${isNew ? 'new' : 'edit'}`}>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 40, textAlign: 'center' }}>
        <div className={`it-expand-icon it-expand-icon--${isNew ? 'new' : 'edit'}`}>
          <FeatherIcon icon={isNew ? 'plus' : 'edit-2'} size={12} color={isNew ? '#1A6B4A' : '#1D4ED8'} />
        </div>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 180 }}>
        <FInput value={row.name} onChange={setName} placeholder="Type name..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 200 }}>
        <FInput value={row.defaultContractor} onChange={set('defaultContractor')} placeholder="Default contractor..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 250 }}>
        <FInput value={row.description} onChange={set('description')} placeholder="Description (optional)..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 170, textAlign: 'center' }}>
        <Tooltip title="Items of this type complete automatically when every unit in the building has a submitted suite inspection this cycle." arrow>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#4A7060', cursor: 'pointer', marginBottom: 0 }}>
            <input type="checkbox" checked={!!row.countsSuiteInspections} onChange={(e) => setCountsSuite(e.target.checked)} style={{ cursor: 'pointer' }} />
            Counts suite inspections
          </label>
        </Tooltip>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 100, textAlign: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: '#6B8F7C' }}>—</span>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 130, textAlign: 'center' }}>
        <div className="it-action-group">
          <button type="button" className="it-btn it-btn--cancel" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InspectionTypesTable = ({ onRefresh }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInspectionTypes(showInactive ? {} : { activeOnly: 'true' });
      const sorted = (res?.data || []).slice().sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
      setTypes(sorted);
    } catch {
      toast.error('Failed to load inspection types');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const handleCreate = async (row) => {
    await createInspectionType({ name: row.name.trim(), defaultContractor: row.defaultContractor.trim(), description: row.description?.trim() || '', countsSuiteInspections: !!row.countsSuiteInspections });
    toast.success('Inspection type created');
    setShowNewRow(false);
    fetchTypes();
    onRefresh?.();
  };

  const handleUpdate = async (id, row) => {
    await updateInspectionType(id, { name: row.name.trim(), defaultContractor: row.defaultContractor.trim(), description: row.description?.trim() || '', countsSuiteInspections: !!row.countsSuiteInspections });
    toast.success('Inspection type updated');
    setEditingId(null);
    fetchTypes();
    onRefresh?.();
  };

  const handleDelete = async (type) => {
    const res = await Swal.fire({
      title: 'Deactivate Inspection Type?',
      html: `<p>Deactivate <strong>${type.name}</strong>? It will no longer appear in inspection forms.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel',
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInspectionType(type._id);
      toast.success('Inspection type deactivated');
      fetchTypes();
      onRefresh?.();
    } catch {
      toast.error('Failed to deactivate inspection type');
    }
  };

  return (
    <Paper elevation={0} className="it-wrap it-paper">
      <div className="it-header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap">
            <FeatherIcon icon="tag" size={18} color="#A7F3D0" />
          </div>
          <div className="it-header__meta">
            <div className="it-header__label">Configuration</div>
            <div className="it-header__title">Inspection Types</div>
          </div>
          {types.length > 0 && (
            <div className="it-header__badges">
              <span className="it-header__badge it-header__badge--records">{types.length} type{types.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        <div className="it-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(167,243,208,0.75)', fontSize: '0.78rem', cursor: 'pointer', marginBottom: 0 }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
            Show inactive
          </label>
          <button type="button" className={`it-btn it-add ${showNewRow ? 'it-btn--danger' : 'it-btn--primary-solid'}`} onClick={() => { setShowNewRow((v) => !v); setEditingId(null); }}>
            <FeatherIcon icon={showNewRow ? 'x' : 'plus'} size={14} />
            {showNewRow ? 'Cancel' : 'Add Type'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="it-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th style={{ minWidth: 180 }}>Name</th>
              <th style={{ minWidth: 200 }}>Default Contractor</th>
              <th style={{ minWidth: 250 }}>Description</th>
              <th style={{ width: 170 }}>Suite inspections</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineTypeRow
                data={{ ...emptyType }}
                isNew
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Spinner color="success" />
                </td>
              </tr>
            )}

            {!loading && types.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={7} className="it-empty-cell">
                  <div className="it-empty" style={{ minHeight: '30vh' }}>
                    <div className="it-empty__icon-wrap"><FeatherIcon icon="tag" size={24} color="#1A6B4A" /></div>
                    <div className="it-empty__title">No Inspection Types</div>
                    <div className="it-empty__sub">Click "Add Type" to create your first inspection type.</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && types.map((type) => {
              if (editingId === type._id) {
                return (
                  <InlineTypeRow
                    key={type._id}
                    data={type}
                    isNew={false}
                    onSave={(row) => handleUpdate(type._id, row)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr key={type._id} className="it-row" onClick={() => setEditingId(type._id)}>
                  <td className="it-expand-td">
                    <div className="it-expand-icon it-expand-icon--view"><FeatherIcon icon="edit-3" size={12} color="#1A6B4A" /></div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: '#1A2D24', fontSize: '0.82rem' }}>{type.name}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.82rem', color: '#4A7060' }}>{type.defaultContractor || '—'}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.80rem', color: '#6B8F7C' }}>{type.description || <span className="it-cell-muted">—</span>}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {countsSuite(type)
                      ? <span className="it-badge it-badge--type">Suite inspections</span>
                      : <span className="it-cell-muted">—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`it-badge ${type.isActive ? 'it-badge--ok' : 'it-badge--overdue'}`}>
                      {type.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="it-actions-td" onClick={(e) => e.stopPropagation()}>
                    <div className="it-action-group">
                      <Tooltip title="Edit" arrow>
                        <button type="button" className="it-ebtn" onClick={(e) => { e.stopPropagation(); setEditingId(type._id); }}>
                          <FeatherIcon icon="edit-2" size={13} color="#1A6B4A" />
                        </button>
                      </Tooltip>
                      <Tooltip title="Deactivate" arrow>
                        <button type="button" className="it-dbtn" onClick={(e) => { e.stopPropagation(); handleDelete(type); }}>
                          <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InspectionTypesTable };